import React, { useCallback, useEffect, useState } from 'react';
import useRetrieveProjectsByOrg from '@/hooks/api/projects/useRetrieveProjectsByOrg.ts';
import { OrgProjectContextType } from './types';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECTS_KEY } from '@/utils/queryKeys.ts';
import OrgProjectContext from '@/context/OrgProjectContext/OrgProjectContext.tsx';
import { useNavigate } from 'react-router-dom';
import { Project } from '@/types/common.ts';

const LAST_USED_ORG_KEY = 'lastUsedOrgId';

const makeProjectStorageKey = (orgId: string) => `lastUsedProject:${orgId}`;

const getOrgIdLocalStorage = () => localStorage.getItem(LAST_USED_ORG_KEY);

const setOrgIdLocalStorage = (orgId: string) => localStorage.setItem(LAST_USED_ORG_KEY, orgId);

const getProjectLocalStorage = (orgId: string) =>
  localStorage.getItem(makeProjectStorageKey(orgId));

const setProjectLocalStorage = (orgId: string, projectId: string) =>
  localStorage.setItem(makeProjectStorageKey(orgId), projectId);

const findOrgIdByProjectId = (projectsByOrg?: Record<string, Project[]>, projectId?: string) => {
  if (!projectsByOrg || !projectId) return undefined;
  for (const [orgId, projects] of Object.entries(projectsByOrg)) {
    const project = projects.find((p) => p.id === projectId);
    if (project) return orgId;
  }
  return undefined;
};

export const OrgProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: projectsByOrg, isPending: loading, error } = useRetrieveProjectsByOrg();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [orgId, setOrgIdState] = useState<string | undefined>(undefined);
  const [projectId, setProjectIdState] = useState<string | undefined>(undefined);

  const setProjectId = useCallback(
    (_orgId: string, _projectId?: string) => {
      const projectsByOrgResults = projectsByOrg?.results;

      if (projectsByOrgResults === undefined || Object.keys(projectsByOrgResults).length === 0) {
        setProjectIdState(undefined);
        return;
      }

      const projects = projectsByOrgResults[_orgId] ?? [];

      if (projects.length === 0) {
        setProjectIdState(undefined);
        return;
      }

      const validProjectId = projects.find((p) => p.id === _projectId)?.id || projects[0].id;

      setProjectLocalStorage(_orgId, validProjectId);
      setProjectIdState(validProjectId);
    },
    [projectsByOrg],
  );

  const setOrgId = useCallback(
    (_orgId?: string, _navigate: boolean = false) => {
      const projectsByOrgResults = projectsByOrg?.results;

      if (projectsByOrgResults === undefined || Object.keys(projectsByOrgResults).length === 0) {
        setOrgIdState(undefined);
        setProjectIdState(undefined);
        return;
      }

      const orgIds = Object.keys(projectsByOrgResults);
      const validOrgId = _orgId && orgIds.includes(_orgId) ? _orgId : orgIds[0];
      setOrgIdLocalStorage(validOrgId);
      setOrgIdState(validOrgId);

      const savedProjectId = getProjectLocalStorage(validOrgId) || undefined;
      setProjectId(validOrgId, savedProjectId);

      if (_navigate) {
        navigate('/automations');
      }
    },
    [navigate, projectsByOrg, setProjectId],
  );

  useEffect(() => {
    const savedOrgId = getOrgIdLocalStorage() || undefined;
    setOrgId(savedOrgId, false);
  }, [projectsByOrg, setOrgId, setProjectId]);

  const previousProjectIdRef = React.useRef<string | undefined>(undefined);

  useEffect(() => {
    if (previousProjectIdRef.current && previousProjectIdRef.current !== projectId) {
      // Project ID has changed
      queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
      // queryClient.removeQueries({ queryKey: PROJECTS_KEY });
    }
    previousProjectIdRef.current = projectId;
  }, [projectId, queryClient]);

  const setProjectIdWithoutOrgId = useCallback(
    (_projectId?: string, _navigate: boolean = false) => {
      const _orgId = findOrgIdByProjectId(projectsByOrg?.results, _projectId);
      if (_orgId) {
        setOrgIdLocalStorage(_orgId);
        setOrgIdState(_orgId);
        setProjectId(_orgId, _projectId);
        if (_navigate) {
          navigate('/automations');
        }
      }
    },
    [navigate, projectsByOrg?.results, setProjectId],
  );

  const value: OrgProjectContextType = {
    orgId,
    setOrgId,
    projectId,
    setProjectId: setProjectIdWithoutOrgId,
    loading,
    error,
  };

  return <OrgProjectContext.Provider value={value}>{children}</OrgProjectContext.Provider>;
};
